import { AccordionItem, AccordionItemContent } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Grid, Skeleton } from '@mui/material'
import React from 'react'
import { getCountryOptions } from 'src/Libs/flow/constants'
import { useCoordinates } from '../../components/Map/coordinates/use-coordinates'
import { LocationMap } from './LocationMap'
import useAccordionStyle from 'src/Libs/styles'
import { useLocationForm } from '../hooks/use-location-form'
import { Select, TextField } from 'src/Libs/inputs/react-hook-form'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { FormSectionTitle } from 'src/Libs/flow/components'
import { useSectionFormStatus } from '../../hooks'

export const Location: React.FC = () => {
  const { t } = useCustomTranslation()
  const { classes } = useAccordionStyle()
  const {
    areAllFieldsEmpty,
    locationData,
    handleCountryChange,
    googleAgreed,
    isUserSettingsLoading,
    timezoneOptions,
    someDataIsFilled,
    hasLocationError,
  } = useLocationForm()
  const geoCodeError = useCoordinates(!!googleAgreed)
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const { isDirty, isError } = useSectionFormStatus(
    dirtyFields => [dirtyFields.generalSettings?.location],
    errors => [errors.generalSettings?.location]
  )

  const [street, houseNumber, postalCode, location] = locationData

  return (
    <AccordionItem
      title={<FormSectionTitle name={t('installation.configOverview.installationLocation')} isDirty={isDirty} isError={isError} />}
      expanded
      className={classes.accordion}
      sx={{ margin: 0 }}
    >
      <AccordionItemContent variant="paper">
        <Grid container columnSpacing={3} py={2}>
          <Grid item xs={12} md={6} lg={5}>
            <div>
              <Grid container rowSpacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    name="generalSettings.location.street"
                    label={t('common.street')}
                    error={!areAllFieldsEmpty && !street?.trim()}
                    disabled={!canEdit}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="generalSettings.location.houseNumber"
                    label={t('common.streetNumber')}
                    error={!areAllFieldsEmpty && !houseNumber?.trim()}
                    disabled={!canEdit}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    name="generalSettings.location.postalCode"
                    label={t('common.zipCode')}
                    error={!areAllFieldsEmpty && !postalCode?.trim()}
                    disabled={!canEdit}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    name="generalSettings.location.location"
                    label={t('common.location')}
                    error={!areAllFieldsEmpty && !location?.trim()}
                    disabled={!canEdit}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={12} lg={6}>
                  <Select
                    name="generalSettings.location.country"
                    label={t('common.country')}
                    options={getCountryOptions(t)}
                    fullWidth
                    disabled={!canEdit}
                    onChange={e => handleCountryChange(e.target.value as string)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={12} lg={6}>
                  <Select
                    name="generalSettings.location.timeZone"
                    label={t('common.timezone')}
                    options={timezoneOptions}
                    fullWidth
                    disabled={!canEdit || timezoneOptions.length === 0}
                  />
                </Grid>
                <Grid item xs={12}>
                  {hasLocationError && <Alert severity="error" text={t('installation.configOverview.missing-location-information')} />}

                  {googleAgreed === false && <Alert severity="info" text={t('installation.configOverview.missingGoogleAgreement')} />}

                  {someDataIsFilled && geoCodeError && <Alert severity="error" text={t('installation.configOverview.missingCoordinates')} />}
                </Grid>
              </Grid>
            </div>
          </Grid>

          <Grid item xs={12} md={6} lg={7}>
            {isUserSettingsLoading ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (
              <LocationMap googleAgreed={!!googleAgreed} isLoading={isUserSettingsLoading} />
            )}
          </Grid>
        </Grid>
      </AccordionItemContent>
    </AccordionItem>
  )
}
